import { Helmet } from "react-helmet-async";
import { RotateCcw } from "lucide-react";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";

const LAST_UPDATED = "October 14, 2026";

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>Refund Policy | SaaSGama</title>
        <meta name="description" content="Read SaaSGama's Refund and Cancellation Policy to understand payment obligations, early cancellation terms, and billing for our SaaS management platform." />
        <meta name="keywords" content="SaaSGama Refund Policy, Cancellation Policy, Refunds, Cancellations, Billing" />
        <link rel="canonical" href="https://saasgama.com/refund-policy" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/refund-policy" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="Refund Policy | SaaSGama" />
        <meta property="og:description" content="Read SaaSGama's Refund and Cancellation Policy to understand payment obligations, early cancellation terms, and billing for our SaaS management platform." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Refund Policy | SaaSGama" />
        <meta name="twitter:description" content="Read SaaSGama's Refund and Cancellation Policy to understand payment obligations, early cancellation terms, and billing for our SaaS management platform." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png" },
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/refund-policy#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Refund Policy", "item": "https://saasgama.com/refund-policy" }
            ]},
            { "@type": "WebPage", "@id": "https://saasgama.com/refund-policy#webpage", "url": "https://saasgama.com/refund-policy", "name": "Refund Policy | SaaSGama", "description": "SaaSGama's refund and cancellation policy, including payment obligations and early cancellation terms.", "breadcrumb": { "@id": "https://saasgama.com/refund-policy#breadcrumb" } }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <RotateCcw className="w-4 h-4" />
            Refunds & Cancellations
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Refund and Cancellation Policy</h1>
          <p className="text-blue-100">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Policy content */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12 space-y-10 text-gray-700 leading-relaxed">

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">1. Overview</h2>
              <p>
                This Refund and Cancellation Policy ("Policy") explains how payment obligations, refunds, and
                subscription cancellations work for SaaSGama ("we," "our," or "us"). It applies to all subscriptions
                purchased through our SaaS management platform, unless a separate client-specific agreement expressly
                states otherwise. By subscribing to SaaSGama, you acknowledge and agree to the terms set out below.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">2. Advance Payment of Subscription Fees</h2>
              <p>
                All subscription fees must be paid in advance for the applicable Subscription Term (whether monthly,
                annual, or any other billing cycle selected at signup or renewal). Your access to the platform for a
                given Subscription Term is conditioned on timely payment of the corresponding fees.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">3. Refund Policy</h2>
              <p>
                All payment obligations are non-cancellable, and all fees paid are non-refundable, except as
                expressly provided in a client-specific agreement, where applicable. This means that once a payment
                has been made for a Subscription Term, SaaSGama is under no obligation to issue a refund for that
                payment, whether in whole or in part, regardless of whether you choose to stop using the platform
                before the end of that term.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">4. Early Cancellation</h2>
              <p>
                Customers may choose to cancel their subscription before the end of the Subscription Term. However,
                cancellation does not entitle customers to a refund of any prepaid fees or unused subscription fees.
                Once cancelled, your subscription will remain active through the end of the Subscription Term for
                which you have already paid, after which it will not renew.
              </p>
              <p className="mt-2">
                Customers remain responsible for paying any outstanding fees due through the end of the applicable
                Subscription Term, in accordance with their subscription agreement. Cancelling your subscription does
                not waive or reduce any fees that were already due or invoiced prior to the cancellation date.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">5. How to Cancel Your Subscription</h2>
              <p>
                To cancel your subscription, contact our team at the email address below or manage your subscription
                directly from your account settings, where available. Cancellation requests are processed on a
                going-forward basis and do not retroactively affect fees already paid or due for the current
                Subscription Term.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">6. Billing Errors and Discrepancies</h2>
              <p>
                If you believe you have been charged in error — for example, a duplicate charge or an incorrect
                amount — please contact us promptly. We will review the billing records for your account and correct
                any verified error. This section addresses genuine billing mistakes only and does not otherwise
                change the non-refundable nature of fees described in Section 3.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">7. Client-Specific Agreements</h2>
              <p>
                Where SaaSGama has entered into a separate written agreement with a customer that expressly addresses
                refunds, cancellation, or early termination, the terms of that agreement will govern over this Policy
                to the extent of any conflict. In all other cases, this Policy applies.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">8. Changes to This Policy</h2>
              <p>
                We may update this Refund and Cancellation Policy from time to time. We will notify customers of
                material changes via email or through the platform. Continued use of our services after such changes
                constitutes acceptance of the updated Policy.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">9. Contact Us</h2>
              <p>If you have questions about this Refund and Cancellation Policy, please contact us:</p>
              <div className="mt-2 space-y-1">
                <p><strong>Email:</strong> hello@saasgama.com</p>
                <p><strong>Address:</strong> 5 Shenton Wy, #10-01, UIC Building, Singapore 068808</p>
                <p><strong>Phone:</strong> +65 8894 3169</p>
              </div>
            </section>

          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
